import Vue from 'vue';
import { Commit, Dispatch } from 'vuex';

type State = Record<string, unknown>;
type Initializer = () => State;
export interface StoreAction {
	action: string;
	module: string;
	fullPath: string;
}
export interface StoreGetter {
	getter: string;
	module: string;
	fullPath: string;
}

const RESET_ACTION_KEY = 'reset';
const RESET_MUTATION_KEY = 'RESET';

const DEFAULT_ACTIONS = [
	{
		actionName: RESET_ACTION_KEY,
		handler: ({ commit }: { commit: Commit }) => {
			commit(RESET_MUTATION_KEY);
		},
	},
];

const DEFAULT_MUTATIONS = [
	{
		mutationName: RESET_MUTATION_KEY,
		// the params are the same in order as the args array of the mutation
		handler: (initializer: Initializer) => (state: State) => {
			const newState = initializer();
			for (const [key, value] of Object.entries(newState)) {
				Vue.set(state, key, value);
			}
		},
		args: ['initializer'],
	},
];

const resetResource = (dispatch: Dispatch, resource: string, useNamespace: boolean) =>
	useNamespace ? dispatch(`${resource}/${RESET_ACTION_KEY}`) : dispatch(RESET_ACTION_KEY);

// Public

const createDefaultMutations = (options: Record<string, unknown>) =>
	DEFAULT_MUTATIONS.reduce<Record<string, unknown>>((mutations, mutation) => {
		const { args: mutationsArgs, mutationName, handler } = mutation;
		// use only the relevant args for this mutation from options
		const args = mutationsArgs.map((arg: string) => options[arg]);
		// @ts-ignore
		mutations[mutationName] = handler(...args);
		return mutations;
	}, {});

const createDefaultActions = () =>
	DEFAULT_ACTIONS.reduce<Record<string, unknown>>((actions, action) => {
		actions[action.actionName] = action.handler;
		return actions;
	}, {});

const resetResources = (dispatch: Dispatch, resources: string[], useNamespace = true) =>
	resources.map((resource) => resetResource(dispatch, resource, useNamespace));

/**
 *
 * @param {*} module - store module namespace
 * @param {*} action - action name
 * @param {*} namespaced - if the module is namespaced or not
 * @returns {object} { action: '', module: '', fullPath: 'module/action' or 'action' (if not namespaced)}
 * fullPath is for using the getter on root context
 */
const buildActionObject = (module: string, action: string, namespaced = true): StoreAction =>
	({
		action,
		module,
		fullPath: namespaced ? `${module}/${action}` : `${action}`,
	} as const);

/**
 *
 * @param {*} module - store module namespace
 * @param {*} getter - getter name
 * @param {*} namespaced - if the module is namespaced or not
 * @returns {object} { getter: '', module: '', fullPath: 'module/getter' or 'getter' (if not namespaced)}
 * fullPath is for using the getter on root context
 */
const buildGetterObject = (module: string, getter: string, namespaced = true): StoreGetter =>
	Object.freeze({
		getter,
		module,
		fullPath: namespaced ? `${module}/${getter}` : `${getter}`,
	});

const StoreModuleService = {
	createDefaultMutations,
	createDefaultActions,
	resetResources,
	buildActionObject,
	buildGetterObject,
};

export default StoreModuleService;
